import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { tenantApi } from '../api/endpoints/tenant';
import { platformApi } from '../api/endpoints/platform';
import { resolvePlatformContainerTenantId } from '../lib/brandingTenant';

/**
 * Mapa roleId → nombre legible del rol. Une el catálogo de roles del tenant
 * (`tenantApi.listRoles`, campo `roleName`) con el catálogo de roles de plataforma
 * (`platformApi.listPlatformRoles`, campo `displayName`), igual que `MyRoleAssignments`.
 *
 * El platform admin (user.tenantId === '*') tiene roles a nivel plataforma (PlatformRole_GT), que se
 * sirven por GET /platform/roles; el resto va a /tenants/{tid}/roles. Además siempre se intentan los
 * roles de plataforma como complemento (best-effort): si el usuario no puede listarlos, esa rama
 * simplemente queda vacía y se usa el catálogo del tenant.
 *
 * Resolver id→nombre reutilizable: cualquier vista que tenga un roleId crudo (auditoría, asignaciones)
 * lo resuelve a su nombre. Si el catálogo no trae el rol, el caller cae a un fallback legible (el
 * `roleName` que venga en la propia fila, o etiqueta + shortId) — NUNCA al UUID crudo.
 */
export function useRoleNameById(): Map<string, string> {
    const { user } = useAuth();
    const isPlatform = user?.tenantId === '*';
    const tid = resolvePlatformContainerTenantId(user?.tenantId);

    const tenantRolesQ = useQuery({
        queryKey: ['tenant', tid, 'roles'],
        queryFn: () => tenantApi.listRoles(tid ?? ''),
        enabled: !isPlatform && !!tid,
    });
    const platformRolesQ = useQuery({
        queryKey: ['platform', 'roles'],
        queryFn: () => platformApi.listPlatformRoles(),
        // El platform admin SIEMPRE necesita estos; el resto los intenta como complemento (best-effort).
        enabled: true,
    });

    const tenantItems = tenantRolesQ.data?.items;
    const platformItems = platformRolesQ.data?.items;

    return useMemo(() => {
        const map = new Map<string, string>();
        // Tenant roles primero; los de plataforma pueden sobreescribir (displayName canónico).
        for (const r of tenantItems ?? []) map.set(r.roleId, r.roleName);
        for (const r of platformItems ?? []) map.set(r.roleId, r.displayName);
        return map;
    }, [tenantItems, platformItems]);
}
